import { AICriterionOutcomeDTO } from './auditCriteriaGenericDTO';

export default class AuditCriteriaSetDTO {
  entityType: number = 0;

  entityId: number = 0;

  isLeaf?: boolean = undefined;

  hasCriteria: boolean = false;

  signature: string = '';

  outOfSync: boolean = false;

  generatedOn?: string = undefined;

  modifiedOn?: string = undefined;

  modifiedById?: string = undefined;

  reviewedOn?: string = undefined;

  reviewedById?: string = undefined;

  // server-owned: a generation input changed after the last review (a standard activated, removed,
  // a requirement link, the control text, a related document), with the latest reason code
  reviewRequiredOn?: string = undefined;

  reviewRequiredReason?: string = undefined;

  // read-only: the standards linked to the entity, the options of a control criterion's standards
  linkedNormIds: number[] | null = null;

  // read-only: the last AI re-evaluation, its trigger, whether one is running, and what re-evaluations
  // changed since the last review
  reevaluatedOn?: string = undefined;

  reevaluatedReason?: string = undefined;

  reevaluating?: boolean = undefined;

  changes: AuditCriteriaChangesDTO | null = null;

  criteria: AuditCriterionDTO[] = [];

  // PLAN-075, read-only: where the criteria come from (AuditCriteriaLayer), the set's own id, the subtype of a
  // subtype set, the generic set a copy was made from and when, when that source last changed, the set's form
  // family and its head version, the "Answered by" bindings and the latest AI outcome per criterion
  layer: number = 0;

  auditCriteriaSetId: number | null = null;

  subType: number | null = null;

  sourceSetId: number | null = null;

  copiedOn?: string = undefined;

  sourceChangedOn?: string = undefined;

  familyId: number | null = null;

  headTaskTypeId: number | null = null;

  headVersion: number | null = null;

  latestOutcomes: AICriterionOutcomeDTO[] = [];
}

export class AuditCriteriaChangesDTO {
  added: number = 0;

  reworded: number = 0;

  archived: number = 0;
}

export class AuditCriterionDTO {
  criterionKey: string = '';

  // server-owned, and left UNDEFINED on a write so it is omitted from the payload: the server fills
  // an absent lineage from the stored criterion, and an empty string is a value that defeats that.
  lineageKey?: string = undefined;

  requirement: string = '';

  successCriteria: string = '';

  required: boolean = true;

  mode: string = 'Recurring';

  verification: string = 'AI';

  sourceLinkId: number | null = null;

  sourceName: string = '';

  sourceKind: string | null = null;

  sourceText: string = '';

  conflictNote: string = '';

  generatedOn?: string = undefined;

  archivedOn?: string = undefined;

  archivedById?: string = undefined;

  requiresComment: boolean = false;

  requiresEvidence: boolean = false;

  // the standards (tenant norm ids) the criterion is tagged with. Read/write: null on a write means
  // "keep what is stored" (the server never converts a legacy criterion on a whole-set save it did
  // not ask for), an empty list is refused for an active control criterion
  normIds: number[] | null = null;

  // read-only: the standards the criterion effectively belongs to (explicit minus stale, or the
  // entity's linked standards when it has none); ignored on a write
  effectiveNormIds: number[] | null = null;

  evidenceTasks: AuditCriterionEvidenceTaskDTO[] = [];

  // read-only: 'New', 'Reworded' or 'Archived' relative to the set's last review; left undefined on a
  // write so it is omitted from the payload
  changeKind?: string = undefined;

  // read-only (PLAN-075 Task 30): 'Verified', 'Pending' or 'Missing' for a one-time criterion of an item that reads a
  // generic set, from the item's latest assessment; null otherwise. Left undefined on a write
  oneTimeState?: string | null = undefined;

  // read and written: the organization's own reference; null clears it
  externalId: string | null = null;

  // read and written: the short header of the criterion's column in the registers, at most 64 characters; null on
  // a write keeps the stored value, so a cleared field is sent as ''
  columnName: string | null = null;

  // read-only, left undefined on a write: the row, its state ('Active', 'NotApplicable', 'Archived'; changed
  // through criteria/{et}/{id}/state only), its trails and its stamps
  auditCriterionId?: number = undefined;

  state?: string = undefined;

  commentTrailId?: number = undefined;

  auditTrailId?: number = undefined;

  created?: string = undefined;

  createdById?: string = undefined;

  modified?: string = undefined;

  modifiedById?: string = undefined;
}

export class AuditCriterionEvidenceTaskDTO {
  taskId: number = 0;

  taskName: string = '';

  mode: string = '';

  recurring: boolean = false;

  taskClosed: boolean = false;

  hidden: boolean = false;

  criterionCount: number = 0;

  aiAdviceStatus: string | null = null;

  aiAdviceOverallStatus: string | null = null;

  aiAdviceOn?: string = undefined;
}

export class EvidenceTaskAdviceDTO {
  taskId: number = 0;

  taskName: string = '';

  generatedOn?: string = undefined;

  overallStatus: string = '';

  markdown: string = '';

  checks: EvidenceTaskAdviceCheckDTO[] = [];
}

export class EvidenceTaskAdviceCheckDTO {
  criterionKey: string = '';

  requirement: string = '';

  status: string = '';

  summary: string = '';
}

export class EvidenceTaskBindingRowDTO {
  entityType: number = 0;

  entityId: number = 0;

  criterionKey: string = '';

  taskId: number = 0;

  taskName: string = '';

  mode: string = '';

  hidden: boolean = false;

  criterionCount: number = 0;
}

export class NormCriteriaStatsRowDTO {
  controlId: number = 0;

  isLeaf?: boolean = undefined;

  hasCriteria: boolean = false;

  criteriaCount: number = 0;

  oneTimeCriteriaCount: number = 0;

  reviewed: boolean = false;

  taskCount: number = 0;

  boundOneTimeCriteriaCount: number = 0;

  verifiedOneTimeCriteriaCount: number = 0;

  overruledTaskCount: number = 0;

  procedureCount: number = 0;

  reviewRequiredOn?: string = undefined;

  reviewRequiredReason?: string = undefined;

  needsReview: boolean = false;

  reevaluatedOn?: string = undefined;

  changesSinceReview: number = 0;
}

export class AuditCriteriaReviewSummaryDTO {
  count: number = 0;

  items: AuditCriteriaReviewSummaryItemDTO[] = [];
}

export class AuditCriteriaReviewSummaryItemDTO {
  entityType: number = 0;

  entityId: number = 0;

  code?: string = undefined;

  name?: string = undefined;

  reviewRequiredOn?: string = undefined;

  reviewRequiredReason?: string = undefined;
}

export class EvidenceTaskBindEditDTO {
  criterionKeys: string[] = [];
}

// Body of POST criteria/{et}/{id}/evidencetasks: the server creates a Hidden task bound to these
// criteria and returns the entity's bindings.
export class CreateEvidenceTaskRequestDTO {
  criterionKeys: string[] = [];

  title: string = '';

  description: string = '';
}

// Body of POST criteria/standard/{id}/generation/start: the items to generate criteria for; empty means
// every control and requirement of the standard.
export class AuditCriteriaGenerationStartDTO {
  items: AuditCriteriaItemKeyDTO[] = [];
}

export class AuditCriteriaGenerationStatusDTO {
  normId: number = 0;

  runStatus: number = 0;

  total: number = 0;

  ready: number = 0;

  generated: number = 0;

  failed: number = 0;

  remaining: number = 0;

  queued: number = 0;

  generic: number = 0;

  items: AuditCriteriaGenerationItemDTO[] = [];
}

export class AuditCriteriaGenerationItemDTO {
  entityType: number = 0;

  entityId: number = 0;

  code?: string = undefined;

  name?: string = undefined;

  status: number = 0;

  criteriaCount?: number = undefined;

  generated?: boolean = undefined;

  generic?: boolean = undefined;

  error?: string = undefined;
}

export class AuditCriteriaEditDTO {
  criteria: AuditCriterionDTO[] = [];
}

// "Archive these criteria": the stamps themselves (archivedOn / archivedById) stay server-owned, so
// the client sends the intent and the keys it applies to, never a date.
// Body of POST criteria/{et}/{id}/state: the criteria to move and their new state.
export class AuditCriterionStateDTO {
  criterionKeys: string[] = [];

  state: string = '';
}

export class AuditCriteriaStatusQueryDTO {
  items: AuditCriteriaItemKeyDTO[] = [];
}

export class AuditCriteriaItemKeyDTO {
  entityType: number = 0;

  entityId: number = 0;
}

export class AuditCriteriaStatusDTO {
  entityType: number = 0;

  entityId: number = 0;

  isLeaf?: boolean = undefined;

  hasCriteria: boolean = false;

  reviewed: boolean = false;

  // PLAN-075: where the item's criteria come from (AuditCriteriaLayer)
  layer?: number = undefined;
}

export class AuditProcedureRefDTO {
  taskId: number = 0;

  name: string = '';
}
